import React from 'react';
import {
  DRAFT_SOURCE_DETAIL_OPTIONS as DRAFT_SOURCE_OPTS,
  FEE_FREQUENCY_DETAIL_OPTIONS as FEE_FREQUENCY_OPTS,
  NON_RENEWAL_PARTY_DETAIL_OPTIONS as NON_RENEWAL_PARTY_OPTS,
  PAYMENT_TERM_DETAIL_OPTIONS as PAYMENT_TERMS_OPTS,
  RENEWAL_TYPE_DETAIL_OPTIONS as RENEWAL_TYPE_OPTS,
} from '../../constants/contracts';
import {
  daysBetween as sharedDaysBetween,
  fmtCurrency,
  fmtDate,
  fmtDateTime,
} from '../../utils/format';

// -----------------------------------------------------------------------
// Formatters
// -----------------------------------------------------------------------

export const formatDate = (dateStr) => fmtDate(dateStr, null);

export const formatDateTime = (dateStr) => fmtDateTime(dateStr, 'N/A');

export const formatCurrency = (value, currency = 'USD') => fmtCurrency(value, currency, { empty: null });

export const formatReadable = (v) => (v || '').replace(/_/g, ' ');

export const daysBetween = (a, b) => sharedDaysBetween(a, b) ?? 0;

// Relative-time formatting for the narrative timeline
export function relativeTime(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const then = new Date(dateStr);
  const diffMs = now - then;
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return '1 week ago';
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 730) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}

// -----------------------------------------------------------------------
// Markdown styling — reused by the Detailed Summary modal
// -----------------------------------------------------------------------

/* eslint-disable jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content */
// react-markdown spreads `children` via {...p} so the lint rules can't
// see the content statically — these are framework-driven wrappers.
export const MARKDOWN_COMPONENTS = {
  h1: (p) => <h1 style={{ fontSize: 20, fontWeight: 500, color: 'var(--text-primary)', marginTop: 24, marginBottom: 12 }} {...p} />,
  h2: (p) => (
    <h2
      style={{
        fontSize: 15,
        fontWeight: 500,
        color: 'var(--text-primary)',
        marginTop: 24,
        marginBottom: 8,
        paddingBottom: 6,
        borderBottom: '0.5px solid var(--border-primary)',
      }}
      {...p}
    />
  ),
  h3: (p) => <h3 style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginTop: 16, marginBottom: 6 }} {...p} />,
  h4: (p) => <h4 style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginTop: 12, marginBottom: 4 }} {...p} />,
  p: (p) => <p style={{ margin: '8px 0', lineHeight: 1.6, color: 'var(--text-primary)' }} {...p} />,
  ul: (p) => <ul style={{ listStyle: 'disc', margin: '8px 0 8px 20px' }} {...p} />,
  ol: (p) => <ol style={{ listStyle: 'decimal', margin: '8px 0 8px 20px' }} {...p} />,
  li: (p) => <li style={{ lineHeight: 1.55, color: 'var(--text-primary)', marginBottom: 2 }} {...p} />,
  strong: (p) => <strong style={{ fontWeight: 500, color: 'var(--text-primary)' }} {...p} />,
  em: (p) => <em style={{ fontStyle: 'italic' }} {...p} />,
  a: (p) => <a style={{ color: 'var(--text-link)', textDecoration: 'underline', textUnderlineOffset: 2 }} target="_blank" rel="noopener noreferrer" {...p} />,
  code: ({ inline, className, children, ...rest }) =>
    inline ? (
      <code
        className="mono"
        style={{ padding: '1px 5px', borderRadius: 4, background: 'var(--surface-alpha-secondary)', fontSize: '0.85em', color: 'var(--text-primary)' }}
        {...rest}
      >
        {children}
      </code>
    ) : (
      <code className={className} {...rest}>{children}</code>
    ),
  hr: () => <hr style={{ margin: '20px 0', border: 'none', borderTop: '0.5px solid var(--border-primary)' }} />,
};
/* eslint-enable jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content */

// Normalize a raw contract value into something safe to show in an input.
// date-valued cells come back as ISO strings; date inputs want YYYY-MM-DD.
export const toDateInput = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return '';
  return d.toISOString().slice(0, 10);
};

// Group the 50+ AI-extracted fields into clause-shaped sections.
// Each field is { label, key, value, display, type, options? }:
//   key      — camelCase contract column (PUT target)
//   value    — raw value (what the <input> edits)
//   display  — formatted string for the read-mode dl
//   type     — 'text' | 'textarea' | 'date' | 'number' | 'select'
export function buildClauses(c) {
  const f = (label, key, type = 'textarea', extra = {}) => {
    const raw = c[key];
    let display = raw;
    if (type === 'date') display = formatDate(raw);
    else if (key === 'contractValue') display = formatCurrency(raw, c.currency);
    else if (['renewalType', 'paymentTerms', 'feeFrequency', 'nonRenewalNoticeParty'].includes(key)) display = formatReadable(raw);
    return { label, key, value: raw, display, type, ...extra };
  };

  const groups = [
    { label: 'Parties & Identity', fields: [
      f('Lightspark entity',     'lightsparkEntity',     'text'),
      f('Counterparty',          'counterparty',         'text'),
      f('Counterparty group',    'counterpartyGroup',    'text'),
      f('Contact name',          'counterpartyContactName',  'text'),
      f('Contact email',         'counterpartyContactEmail', 'text'),
      f('Contact phone',         'counterpartyContactPhone', 'text'),
      f('Contact role',          'counterpartyContactRole',  'text'),
      f('Counterparty address',  'counterpartyAddress',  'textarea'),
      f('Lightspark address',    'lightsparkAddress',    'textarea'),
      f('Short name',            'shortName',            'text'),
      f('Internal owner',        'internalOwner',        'text'),
      // Changing this also renames the draft files in Drive to match —
      // handled server-side in the PUT /api/contracts/:id handler.
      f('Draft source',          'draftSource',          'select', { options: DRAFT_SOURCE_OPTS }),
    ] },
    { label: 'Term & Renewal', fields: [
      f('Effective date',             'effectiveDate',            'date'),
      f('Executed date',              'executedDate',             'date'),
      f('Expiration date',            'expirationDate',           'date'),
      f('Initial term',               'initialTerm',              'text'),
      f('Renewal type',               'renewalType',              'select', { options: RENEWAL_TYPE_OPTS }),
      f('Renewal term',               'renewalTerm',              'text'),
      f('Renewal notice days',        'renewalNoticeDays',        'number'),
      f('Auto-renewal notice date',   'autoRenewalNoticeDate',    'date'),
      // Recording a non-renewal notice tells the expiry sweep to stop rolling
      // this contract and let it expire at term end (doc-375).
      f('Non-renewal notice date',    'nonRenewalNoticeDate',     'date'),
      f('Non-renewal notice by',      'nonRenewalNoticeParty',    'select', { options: NON_RENEWAL_PARTY_OPTS }),
      f('Termination notice days',    'terminationNoticeDays',    'number'),
      f('Termination for convenience','terminationForConvenience','textarea'),
      f('Termination for cause',      'terminationForCause',      'textarea'),
      f('Survival clauses',           'survivalClauses',          'textarea'),
    ] },
    { label: 'Commercial', fields: [
      f('Contract value',  'contractValue', 'number'),
      f('Currency',        'currency',      'text'),
      f('Payment terms',   'paymentTerms',  'select', { options: PAYMENT_TERMS_OPTS }),
      f('Fee structure',   'feeStructure',  'textarea'),
      f('Fee frequency',   'feeFrequency',  'select', { options: FEE_FREQUENCY_OPTS }),
      f('Fee escalation',  'feeEscalation', 'textarea'),
      f('Late fees',       'lateFees',      'textarea'),
    ] },
    { label: 'Liability & Risk', fields: [
      f('Liability cap',                 'liabilityCap',               'textarea'),
      f('Cap exceptions',                'liabilityCapExceptions',     'textarea'),
      f('Consequential damages waiver',  'consequentialDamagesWaiver', 'textarea'),
      f('Indemnification',               'indemnification',            'textarea'),
      f('Insurance requirements',        'insuranceRequirements',      'textarea'),
    ] },
    { label: 'IP & Data', fields: [
      f('IP ownership',            'ipOwnership',          'textarea'),
      f('License grants',          'licenseGrants',        'textarea'),
      f('Data protection',         'dataProtection',       'textarea'),
      f('Data access level',       'dataAccessLevel',      'text'),
      f('Data location',           'dataLocation',         'text'),
      f('Security requirements',   'securityRequirements', 'textarea'),
    ] },
    { label: 'Confidentiality & Covenants', fields: [
      f('Confidentiality duration', 'confidentialityDuration', 'text'),
      f('Non-compete',              'nonCompete',              'textarea'),
      f('Non-solicitation',         'nonSolicitation',         'textarea'),
      f('Exclusivity',              'exclusivity',             'textarea'),
    ] },
    { label: 'Operational', fields: [
      f('Audit rights',             'auditRights',            'textarea'),
      f('Assignment restrictions',  'assignmentRestrictions', 'textarea'),
      f('Change of control',        'changeOfControl',        'textarea'),
      f('Dispute resolution',       'disputeResolution',      'textarea'),
      f('SLA terms',                'slaTerms',               'textarea'),
      f('Force majeure',            'forceMajeure',           'textarea'),
    ] },
    { label: 'Legal Venue', fields: [
      f('Governing law', 'governingLaw', 'text'),
      f('Jurisdiction',  'jurisdiction', 'text'),
    ] },
  ];

  // Coarse flagging — Liability & Risk section on an active unsigned-
  // or-unspec'd contract carries higher stakes; mark Risk if missing.
  const risksJoined = (c.keyRisks || []).join(' ').toLowerCase();
  return groups.map((g, i) => {
    const filled = g.fields.filter(fd => fd.value != null && fd.value !== '');
    let flag = null;
    if (g.label === 'Liability & Risk' && c.status === 'active' && !c.liabilityCap) flag = 'risk';
    if (risksJoined && g.fields.some(fd => risksJoined.includes(fd.label.toLowerCase()))) flag = flag || 'attention';
    return {
      num: String(i + 1),
      label: g.label,
      fields: g.fields,
      filled,
      flag,
    };
  });
}

// Convert a string from an input back to the shape the API expects.
export function serializeClauseField(fd, raw) {
  if (raw === '' || raw === null || raw === undefined) return null;
  if (fd.type === 'number') {
    const n = Number(raw);
    return isNaN(n) ? null : n;
  }
  if (fd.type === 'date') {
    // Input gives YYYY-MM-DD; store as ISO midnight UTC (matches other dates).
    return raw ? new Date(raw + 'T00:00:00Z').toISOString() : null;
  }
  return raw;
}

// Shape a server-side activity row ({ kind, timestamp, userName, details })
// into the { when, who, text } shape the Timeline tab renders. Keeps the
// visual identical to the old status-history-only feed but enriches the
// narrative with uploads + creation context.
export function formatActivityEvent(e) {
  const who = e.userName || e.userEmail || 'System';
  let text = '';
  if (e.kind === 'created') {
    text = e.details?.isWizard
      ? 'generated this contract via the self-serve wizard.'
      : 'created this contract record.';
  } else if (e.kind === 'file_uploaded') {
    const dv = e.details?.draftVersion ? ` ${e.details.draftVersion}` : '';
    const dir = e.details?.direction === 'inbound'
      ? ' (counterparty markup)'
      : e.details?.direction === 'outbound'
        ? ' (sent to counterparty)'
        : '';
    if (e.details?.isExecuted) {
      text = `uploaded the executed version${dir}.`;
    } else {
      text = `uploaded draft${dv}${dir}.`;
    }
  } else if (e.kind === 'status_changed') {
    text = e.details?.oldStatus
      ? `changed status from ${formatReadable(e.details.oldStatus)} to ${formatReadable(e.details.newStatus)}.`
      : `created the contract as ${formatReadable(e.details?.newStatus || 'draft')}.`;
  }
  return { when: e.timestamp, who, text, kind: e.kind, userRole: e.userRole };
}

// Build a narrative log from the status history, sorted newest first.
export function buildTimeline(c) {
  const out = [];
  const history = Array.isArray(c.statusHistory) ? [...c.statusHistory].reverse() : [];
  history.forEach(h => {
    out.push({
      when: h.changedAt,
      who: h.changedByName || 'System',
      text: h.oldStatus
        ? `changed status from ${formatReadable(h.oldStatus)} to ${formatReadable(h.newStatus)}.`
        : `created the contract as ${formatReadable(h.newStatus)}.`
    });
  });
  if (c.updatedAt && c.updatedByName && (!history.length || new Date(c.updatedAt) > new Date(history[0]?.changedAt || 0))) {
    out.unshift({ when: c.updatedAt, who: c.updatedByName, text: 'updated the contract.' });
  }
  if (c.createdAt && !history.length) {
    out.push({ when: c.createdAt, who: c.createdByName || 'System', text: 'created this contract record.' });
  }
  return out;
}

export function buildFileTiles(contract, contractFiles) {
  if (!contract) return [];
  const out = [];

  if (contractFiles && contractFiles.length > 0) {
    const sorted = [...contractFiles].sort((a, b) => {
      if (!!b.isExecuted !== !!a.isExecuted) return b.isExecuted ? 1 : -1;
      return (a.uploadedAt || '').localeCompare(b.uploadedAt || '');
    });

    for (const file of sorted) {
      const isReference = file.fileKind === 'reference';
      const label = isReference
        ? 'Reference'
        : (file.isExecuted ? 'Executed' : (file.draftVersion || 'Draft'));
      const uploadedDate = file.uploadedAt ? formatDate(file.uploadedAt) : null;
      const primaryDate = file.documentDate
        ? `doc dated ${formatDate(file.documentDate)}`
        : (uploadedDate ? `uploaded ${uploadedDate}` : null);
      const hint = primaryDate ? `${label} · ${primaryDate}` : label;

      out.push({
        key: `file-${file.id}`,
        fileId: file.id,
        driveFileId: file.driveFileId || null,
        name: file.filename || file.originalFilename || 'Document.pdf',
        hint,
        // Date portion alone, so executed files can show the status as a chip
        // by the title and keep only the date in the subline (no duplication).
        dateLabel: primaryDate,
        href: file.driveWebViewLink || null,
        tone: 'pdf',
        signed: !!file.isExecuted,
        fileKind: file.fileKind || 'lifecycle',
        version: label,
        documentDate: file.documentDate || null,
        uploadedAt: file.uploadedAt || null,
        direction: file.direction || null,
        fileNotes: file.fileNotes || null,
      });
    }

    return out;
  }

  if (contract.driveWebViewLink) {
    out.push({
      key: 'drive-file',
      name: contract.title ? `${contract.title}.pdf` : 'Signed document.pdf',
      hint: contract.executedDate ? `Updated ${formatDate(contract.executedDate)}` : 'In Drive',
      href: contract.driveWebViewLink,
      tone: 'pdf',
      signed: !!contract.executedDate,
    });
  }
  if (contract.fileUrl) {
    out.push({
      key: 'upload',
      name: 'Uploaded document',
      hint: contract.createdAt ? `Uploaded ${formatDate(contract.createdAt)}` : null,
      href: contract.fileUrl,
      tone: 'pdf',
      signed: false,
    });
  }

  return out;
}
